/**
 * A module's identity: what it is called, what it is worth, whether it is an
 * elective, and what it covers.
 *
 * @module components/studio/panels/DetailsPanel
 */

import React from "react";

import type { ModuleEditorState } from "@/components/studio/useModuleEditor";
import { Checkbox, Panel } from "@/components/ui";
import { FormField, FormGrid, TextAreaField, TextField } from "@/components/ui/Form";
import type { Module } from "@/types";

export function DetailsPanel({
  module,
  updateDraft,
}: {
  module: Module;
  updateDraft: ModuleEditorState["updateDraft"];
}): React.ReactElement {
  return (
    <Panel
      aria-label="Module details"
      description="Enter the code, title, credit value, classification, and what the module covers."
    >
      <FormGrid columns={4}>
        <FormField
          label="Module title"
          htmlFor="module-workspace-title"
          helpText="Enter a clear module title that learners will understand. It appears in schedules and exports."
          required
          className="@md:col-span-2"
        >
          <TextField
            id="module-workspace-title"
            value={module.title ?? ""}
            onChange={(value) =>
              updateDraft((draft) => {
                draft.title = value;
              })
            }
            required
            data-testid="module-workspace-title"
          />
        </FormField>
        <FormField
          label="Module code"
          htmlFor="module-workspace-code"
          helpText="Optional. A short code for timetables, mapping tables, and workflow summaries."
        >
          <TextField
            id="module-workspace-code"
            value={module.code ?? ""}
            onChange={(value) =>
              updateDraft((draft) => {
                draft.code = value;
              })
            }
          />
        </FormField>
        <FormField
          label="Credits"
          htmlFor="module-workspace-credits"
          helpText="Enter the module credits. They are used to work out workload, effort hours, and progression."
          required
        >
          <TextField
            id="module-workspace-credits"
            type="number"
            min={0}
            step={1}
            value={module.credits || ""}
            onChange={(value) =>
              updateDraft((draft) => {
                draft.credits = Math.max(0, Number(value) || 0);
              })
            }
            required
            suffix="credits"
            data-testid="module-workspace-credits"
          />
        </FormField>
        <FormField
          label="Module synopsis"
          htmlFor="module-workspace-synopsis"
          helpText="Summarise the module purpose, scope, and learner focus in plain language."
          className="@md:col-span-2"
        >
          <TextAreaField
            id="module-workspace-synopsis"
            value={module.synopsis ?? ""}
            onChange={(value) =>
              updateDraft((draft) => {
                draft.synopsis = value;
              })
            }
            rows={4}
            className="w-full"
          />
        </FormField>
        <FormField
          label="Indicative content"
          htmlFor="module-workspace-content"
          helpText="Outline the topics, skills, and learning activities this module covers."
          className="@md:col-span-2"
        >
          <TextAreaField
            id="module-workspace-content"
            value={module.indicativeContent ?? ""}
            onChange={(value) =>
              updateDraft((draft) => {
                draft.indicativeContent = value;
              })
            }
            rows={4}
            className="w-full"
          />
        </FormField>
        <div className="@md:col-span-2 @4xl:col-span-4">
          <Checkbox
            id="module-workspace-elective"
            label="Elective module"
            checked={Boolean(module.isElective)}
            onChange={(checked) =>
              updateDraft((draft) => {
                draft.isElective = checked;
              })
            }
          />
        </div>
      </FormGrid>
    </Panel>
  );
}

export default DetailsPanel;
